import React, { useContext } from 'react';
import { Typography, Divider, Card, Space, Drawer } from 'antd';
import { ApplicationUIContext, ButtonGroup, useDrawerState, helpers } from '@yuhuinc/ui-library';
import Applicants from './overview/Applicants';
import LeaseSummary from './overview/LeaseSummary';
import AssignedAgent from './overview/AssignedAgent';
import Stats from './overview/Stats';
import RentableItemsSummary from './overview/RentableItemsSummary';
import CancelApplicationForm from './overview/CancelApplicationForm';
import AssignNewUnitButton from './overview/AssignNewUnitButton';
import { StyledHeader } from '../common/Styles';

import { getTranslatedString } from 'helpers/i18n';
import InviteApplicantsButton from '../common/actions/InviteApplicants';
import CreateLeaseButton from '../common/actions/CreateLeaseButton';

import { CurrentApplicationContext } from '../ApplicationOverview';
import RemindAllApplicantsButton from './overview/RemindAllApplicantsButton';

import {
  canCancelApplication,
  canCreateLeaseForApplication,
  canAssignNewUnitForApplication,
} from '../common/helpers';

const { WIDE_CARD_WIDTH } = helpers;

const { Title } = Typography;

const CancelText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.CancelApplication'
);

const Overview = () => {
  const { isMobile } = useContext(ApplicationUIContext);
  const currentApplication = useContext(CurrentApplicationContext);
  const [, cancelApplicationDrawer] = useDrawerState(false);

  const canInviteAll =
    ['in_progress'].includes(currentApplication.lifecycle?.status) &&
    ['draft', 'in_progress'].includes(currentApplication.lifecycle?.substatus);

  const canRemindAll = currentApplication?.applicants?.some((applicant) =>
    ['not_started', 'in_progress', 'adding_details'].includes(applicant?.lifecycle?.substatus)
  );

  const canCancel = canCancelApplication(currentApplication);
  const canCreateLease = canCreateLeaseForApplication(currentApplication);
  const canAssignNewUnit = canAssignNewUnitForApplication(currentApplication);

  return (
    <Card style={{ maxWidth: WIDE_CARD_WIDTH }}>
      <StyledHeader>
        <Title level={isMobile ? 3 : 4}>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.cardTitle'
          )}
        </Title>
        <ButtonGroup
          buttons={[
            canAssignNewUnit && <AssignNewUnitButton key="assign_new_unit" />,
            canInviteAll && <InviteApplicantsButton key="invite_applicants" />,
            canRemindAll && <RemindAllApplicantsButton key="remind_applicants" />,
            canCreateLease && <CreateLeaseButton key="create_lease" />,
            canCancel && {
              label: CancelText,
              onClick: cancelApplicationDrawer.open,
            },
          ].filter(Boolean)}
        />
      </StyledHeader>

      <Divider />

      <Space direction="vertical" size="middle">
        <Stats />
        <AssignedAgent />
      </Space>

      <Divider />

      <Applicants />

      <Divider />

      <LeaseSummary />

      <Divider />

      <RentableItemsSummary />
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.title'
        )}
        visible={cancelApplicationDrawer.isVisible}
        closable
        destroyOnClose
        onClose={cancelApplicationDrawer.close}
      >
        <CancelApplicationForm afterSubmit={cancelApplicationDrawer.close} />
      </Drawer>
    </Card>
  );
};

export default Overview;
